import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import UserAccessList from "./UserAccessList";
import { normalizeSharingUsers } from "../adapters";
import type { MCPSharing } from "../../../services/agent-tools-service";
import { useTranslation } from "react-i18next";

type Props = {
  connectionId: string;
  sharing: MCPSharing | undefined | null;
};

const AccessLevelSection = ({ connectionId, sharing }: Props) => {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const ChevronIcon = open ? ChevronDown : ChevronRight;

  const isUserScope = sharing?.scope === "user";

  const users = useMemo(
    () =>
      isUserScope
        ? normalizeSharingUsers((sharing as { items?: unknown }).items as never)
        : [],
    [isUserScope, sharing],
  );

  const projectName =
    sharing?.scope === "project" ? sharing.name?.trim() : "";

  const scopeLabel =
    sharing?.scope === "organization"
      ? t("mcpPage.accessScope.organization")
      : sharing?.scope === "project"
        ? projectName || t("mcpPage.accessScope.selectedProject")
        : t("mcpPage.accessScope.selectedUsers");

  return (
    <section className="px-6 pb-5">
      <h3 className="mb-3 text-sm font-medium text-foreground">{t("mcpPage.fields.accessLevel")}</h3>

      {isUserScope ? (
        <>
          <button
            type="button"
            onClick={() => setOpen((value) => !value)}
            className="flex w-full items-center gap-2 text-sm text-foreground"
            aria-expanded={open}
          >
            <ChevronIcon className={`size-4 text-muted-foreground ${i18n.dir() === "rtl" && !open ? "rotate-180" : ""}`} />
            <span>{t("mcpPage.accessScope.selectedUsers")}</span>
            <span className="rounded-full bg-muted px-2 py-0.5 text-xs text-muted-foreground">
              {users.length}
            </span>
          </button>
          {open && (
            <UserAccessList connectionId={connectionId} users={users} />
          )}
        </>
      ) : (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <span>{scopeLabel}</span>
        </div>
      )}
    </section>
  );
};

export default AccessLevelSection;
